/**
 * OMEGA brain & organ registry — UI-only data per
 * `docs/OMEGA-DASHBOARD-SPEC.md` §9.1, §9.2.
 *
 * Phase 1 scope (Window 5 OMEGA dashboard):
 *   - Production Brain enumerates its six Factory specialists (the
 *     real WASM modules now in `crates/gi-engine/src/specialists/`).
 *   - All five other brains render a "Coming soon" skeleton at L2.
 *
 * Adding a 7th brain or a new organ to a brain is a single entry
 * in these maps — no component changes.
 */

export type OrganStatus = 'active' | 'idle' | 'error';
export type BrainStatus = 'active' | 'paused';

export interface BrainEntry {
  /** Stable id, also the URL slug. */
  id: string;
  /** Display name on rails, breadcrumbs, headers. */
  name: string;
  /** One-sentence purpose. */
  description: string;
  /** When false, L2 renders the "Coming soon" skeleton. */
  active: boolean;
  /** Pretty status badge value. */
  status: BrainStatus;
}

export interface OrganEntry {
  /** Stable id, also the URL slug under the brain. */
  id: string;
  /** Brain this organ belongs to. */
  brain_id: string;
  /** Display name. */
  name: string;
  /** One-sentence purpose. */
  description: string;
  /** Typed input description (e.g. `"segment name"`). */
  input_type: string;
  /** Typed output description (e.g. `"MarketSignal"`). */
  output_type: string;
  /** Latest health badge. */
  status: OrganStatus;
  /** Free-form short label for the most recent thing the organ did. */
  last_action: string;
  /** ISO 8601 timestamp of `last_action`. */
  last_action_at: string;
}

export const BRAIN_REGISTRY: BrainEntry[] = [
  {
    id: 'production',
    name: 'Production Brain',
    description: 'Designs, builds, and ships new GI products.',
    active: true,
    status: 'active',
  },
  {
    id: 'knowledge',
    name: 'Knowledge Brain',
    description: 'Pattern scanners and naming-authority organs.',
    active: false,
    status: 'paused',
  },
  {
    id: 'analytics',
    name: 'Analytics Brain',
    description: 'Aggregates and projects engine telemetry.',
    active: false,
    status: 'paused',
  },
  {
    id: 'decision',
    name: 'Decision Brain',
    description: 'Refusal-list arbitration and per-request gating.',
    active: false,
    status: 'paused',
  },
  {
    id: 'defense',
    name: 'Defense Brain',
    description: 'Drift, anomaly, and adversarial-input watchers.',
    active: false,
    status: 'paused',
  },
  {
    id: 'discovery',
    name: 'Discovery Brain',
    description: 'Combination labs and capability research.',
    active: false,
    status: 'paused',
  },
];

const NOW = new Date().toISOString();

export const ORGAN_REGISTRY: OrganEntry[] = [
  {
    id: 'market_research',
    brain_id: 'production',
    name: 'MarketResearch',
    description: 'Scans the marketplace for products in a target segment.',
    input_type: 'segment name',
    output_type: 'MarketSignal',
    status: 'active',
    last_action: 'scanned segment "capital-markets"',
    last_action_at: NOW,
  },
  {
    id: 'gap_analyzer',
    brain_id: 'production',
    name: 'GapAnalyzer',
    description: 'Compares MarketSignals against known recipes.',
    input_type: 'MarketSignal',
    output_type: 'ProductGap',
    status: 'active',
    last_action: 'identified 4 missing recipes in "capital-markets"',
    last_action_at: NOW,
  },
  {
    id: 'product_builder',
    brain_id: 'production',
    name: 'ProductBuilder',
    description: 'Drafts manifests + contract skeletons for product gaps.',
    input_type: 'ProductGap',
    output_type: 'ProductDraft',
    status: 'idle',
    last_action: 'drafted manifest skeleton for cm-tokenized-bond-issuance',
    last_action_at: NOW,
  },
  {
    id: 'qa_tester',
    brain_id: 'production',
    name: 'QaTester',
    description: 'Validates ProductDraft schema and runs sample inputs.',
    input_type: 'ProductDraft',
    output_type: 'QaReport',
    status: 'idle',
    last_action: 'validated cm-tokenized-bond-issuance — passed',
    last_action_at: NOW,
  },
  {
    id: 'publisher',
    brain_id: 'production',
    name: 'Publisher',
    description: 'Stages products for marketplace; requires human cosign.',
    input_type: 'QaReport (must pass)',
    output_type: 'PublicationRecord',
    status: 'idle',
    last_action: 'staged cm-tokenized-bond-issuance — awaiting cosign',
    last_action_at: NOW,
  },
  {
    id: 'monitor',
    brain_id: 'production',
    name: 'Monitor',
    description: 'Threshold-checks installed product health and surfaces alerts.',
    input_type: 'PublicationRecord + HealthSample',
    output_type: 'MonitorAlert',
    status: 'active',
    last_action: 'no alerts in last 60s',
    last_action_at: NOW,
  },
];

export function findBrain(id: string | undefined): BrainEntry | null {
  if (!id) return null;
  return BRAIN_REGISTRY.find((b) => b.id === id) ?? null;
}

export function findOrgan(brainId: string | undefined, organId: string | undefined): OrganEntry | null {
  if (!brainId || !organId) return null;
  return ORGAN_REGISTRY.find((o) => o.brain_id === brainId && o.id === organId) ?? null;
}

export function organsForBrain(brainId: string): OrganEntry[] {
  return ORGAN_REGISTRY.filter((o) => o.brain_id === brainId);
}
